import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, MapPin, Plus, Users, Video } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { fetchShoots, fetchClients, createShootSession, fetchShootCrewCandidates, fetchJoinableCreatives, qk, type ShootSession } from '@/lib/queries';
import { useTasksOnly, useCan } from '@/hooks/use-scopes';
import { cn } from '@/lib/cn';

type Range = 'week' | 'fortnight' | 'month' | 'past';

const STATUS_CLS: Record<string, string> = {
  proposed: 'bg-amber-500/15 text-amber-300', scheduled: 'bg-blue-500/15 text-blue-300', planned: 'bg-blue-500/15 text-blue-300',
  declined: 'bg-red-500/15 text-red-300', done: 'bg-emerald-500/15 text-emerald-300', cancelled: 'bg-slate-700/40 text-slate-400',
};
const STATUS_LABEL: Record<string, string> = { proposed: 'proposed', scheduled: 'scheduled', planned: 'scheduled', declined: 'declined', done: 'done', cancelled: 'cancelled' };

function bounds(r: Range): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (r === 'past') {
    const from = new Date(start); from.setDate(from.getDate() - 30);
    return { from: from.toISOString(), to: start.toISOString() };
  }
  const days = r === 'week' ? 7 : r === 'fortnight' ? 14 : 31;
  const to = new Date(start); to.setDate(to.getDate() + days);
  return { from: start.toISOString(), to: to.toISOString() };
}

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

export default function ShootsPage() {
  const qc = useQueryClient();
  const [range, setRange] = useState<Range>('fortnight');
  const [clientId, setClientId] = useState('');
  const [creating, setCreating] = useState(false);
  const tasksOnly = useTasksOnly();
  const wide = tasksOnly === false;
  const canPlan = useCan('shoots.plan');
  const b = useMemo(() => bounds(range), [range]);
  const clients = useQuery({ queryKey: qk.clients, queryFn: fetchClients, enabled: wide });
  const shoots = useQuery({ queryKey: ['shoots', b.from, b.to, clientId], queryFn: () => fetchShoots(b.from, b.to, clientId || undefined) });

  // new shoot form
  const [fClient, setFClient] = useState('');
  const [fWhen, setFWhen] = useState('');
  const [fWhere, setFWhere] = useState('');
  const [fTitle, setFTitle] = useState('');
  const [fNotes, setFNotes] = useState('');
  const [fCrew, setFCrew] = useState<string[]>([]);
  const [fHours, setFHours] = useState('4');
  const [fCreatives, setFCreatives] = useState<string[]>([]);
  const fAt = fWhen ? new Date(fWhen).toISOString() : undefined;
  const fMinutes = Math.round(Math.max(0.5, Math.min(16, Number(fHours) || 4)) * 60);
  // Who is free at that time: the server marks a videographer busy when another
  // live shoot overlaps the window, and refuses them outright if picked anyway.
  const candidates = useQuery({ queryKey: ['shoot-crew', fClient, fAt, fMinutes], queryFn: () => fetchShootCrewCandidates(fClient, fAt, fMinutes), enabled: creating && !!fClient });
  const joinable = useQuery({ queryKey: ['shoot-joinable', fClient], queryFn: () => fetchJoinableCreatives(fClient), enabled: creating && !!fClient });
  // The days around the chosen one, so a clash is seen before it is picked.
  const around = useMemo(() => {
    if (!fWhen) return null;
    const d = new Date(fWhen); const from = new Date(d); from.setDate(from.getDate() - 3); from.setHours(0, 0, 0, 0);
    const to = new Date(d); to.setDate(to.getDate() + 4); to.setHours(0, 0, 0, 0);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [fWhen]);
  const nearby = useQuery({ queryKey: ['shoots', 'around', around?.from, around?.to], queryFn: () => fetchShoots(around!.from, around!.to), enabled: creating && !!around });
  const create = useMutation({
    mutationFn: () => createShootSession({ client_id: fClient, shoot_at: new Date(fWhen).toISOString(), duration_minutes: fMinutes, location: fWhere, title: fTitle, notes: fNotes, crew_user_ids: fCrew, creative_ids: fCreatives }),
    onSuccess: (r) => {
      toast.success(r.status === 'scheduled' ? `Shoot scheduled: ${r.title}` : `Proposed: ${r.title}. ${r.approver_names.length ? r.approver_names.join(', ') + ' will approve it.' : 'Waiting for approval.'}`);
      setCreating(false); setFWhen(''); setFWhere(''); setFTitle(''); setFNotes(''); setFCrew([]); setFCreatives([]); qc.invalidateQueries({ queryKey: ['shoots'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const groups = useMemo(() => {
    const m = new Map<string, ShootSession[]>();
    for (const s of shoots.data ?? []) {
      const key = new Date(s.shoot_at).toLocaleDateString('en-AE', { weekday: 'long', day: 'numeric', month: 'long' });
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(s);
    }
    return Array.from(m.entries());
  }, [shoots.data]);

  return (
    <div>
      <PageHeader title="Shoots" subtitle="Each shoot is a session with the creatives being filmed. A creative joins once its script is approved."
        actions={canPlan && !creating ? <Button icon={<Plus size={14} />} onClick={() => { setCreating(true); if (!fClient && clients.data?.length) setFClient(clients.data[0].id); }} data-testid="propose-shoot">Propose a shoot</Button> : undefined} />

      {creating && (
        <Card className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select label="Clinic" value={fClient} onChange={setFClient} searchable options={(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
            <Input label="Date and time" type="datetime-local" value={fWhen} onChange={(e) => setFWhen(e.target.value)} data-testid="shoot-when" />
            <Input label="Length (hours)" type="number" min={0.5} max={16} step={0.5} value={fHours} onChange={(e) => setFHours(e.target.value)} data-testid="shoot-hours" />
            <Input label="Location" placeholder="Clinic, room, address" value={fWhere} onChange={(e) => setFWhere(e.target.value)} />
            <Input label="Title (optional)" placeholder="e.g. October reels day" value={fTitle} onChange={(e) => setFTitle(e.target.value)} />
            <div className="sm:col-span-2"><Input label="Notes for the crew" placeholder="Wardrobe, props, parking, patient consents..." value={fNotes} onChange={(e) => setFNotes(e.target.value)} /></div>
            {around && (
              <div className="sm:col-span-2 rounded-lg bg-bg px-3 py-2 text-xs" data-testid="shoot-around">
                <div className="text-[10px] uppercase tracking-wide text-muted mb-1">Shoots around that day</div>
                {(nearby.data ?? []).filter((s) => s.status === 'proposed' || s.status === 'scheduled' || s.status === 'planned').length === 0
                  ? <div className="text-muted">Nothing else in those days.</div>
                  : (nearby.data ?? []).filter((s) => s.status === 'proposed' || s.status === 'scheduled' || s.status === 'planned').map((s) => (
                    <div key={s.id} className="flex flex-wrap gap-x-2 text-muted-strong">
                      <span className="font-mono">{new Date(s.shoot_at).toLocaleString('en-AE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <span>{s.title}</span><span className="text-muted">· {s.crew.map((m) => m.name).join(', ') || 'no crew'} · {STATUS_LABEL[s.status]}</span>
                    </div>
                  ))}
              </div>
            )}
            <div className="sm:col-span-2">
              <div className="text-xs text-muted mb-1.5">Videographer or photographer</div>
              {!fClient && <div className="text-xs text-muted">Pick the clinic first.</div>}
              {fClient && (candidates.data ?? []).length === 0 && <div className="text-xs text-muted">No videographer or photographer on the team yet.</div>}
              <div className="flex flex-wrap gap-2">
                {(candidates.data ?? []).map((u) => {
                  const busy = (u.busy ?? []).length > 0;
                  return (
                    <label key={u.id} data-testid="crew-option" data-busy={busy ? 'true' : 'false'} title={busy ? `On ${u.busy[0].title} at that time` : undefined}
                      className={cn('inline-flex items-center gap-2 text-xs px-3 py-1.5 min-h-[36px] rounded-full border', busy ? 'border-slate-800 text-muted/50 cursor-not-allowed line-through' : 'cursor-pointer', !busy && (fCrew.includes(u.id) ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted'))}>
                      <input type="checkbox" className="accent-accent-400" disabled={busy} checked={fCrew.includes(u.id)} onChange={() => setFCrew((c) => c.includes(u.id) ? c.filter((x) => x !== u.id) : [...c, u.id])} />
                      {u.display_name}<span className="text-[10px] opacity-70">{busy ? `on ${u.busy[0].title}` : u.designations.join(', ')}{u.on_client || busy ? '' : ' · not on this clinic yet'}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-xs text-muted mb-1.5">Creatives to film (script approved, not on another shoot)</div>
              {fClient && (joinable.data ?? []).filter((c) => c.eligible).length === 0 && <div className="text-xs text-muted">Nothing ready to film on this clinic yet.</div>}
              <div className="flex flex-wrap gap-2">
                {(joinable.data ?? []).filter((c) => c.eligible).map((c) => (
                  <label key={c.id} data-testid="shoot-creative-option" className={cn('inline-flex items-center gap-2 text-xs px-3 py-1.5 min-h-[36px] rounded-full border cursor-pointer', fCreatives.includes(c.id) ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted')}>
                    <input type="checkbox" className="accent-accent-400" checked={fCreatives.includes(c.id)} onChange={() => setFCreatives((x) => x.includes(c.id) ? x.filter((y) => y !== c.id) : [...x, c.id])} />
                    <span className="font-mono">{c.code}</span><span className="truncate max-w-[24ch]">{c.title}</span>
                  </label>
                ))}
              </div>
              {/* the rest, with what is missing: hiding them read as "this piece cannot be filmed" */}
              {(joinable.data ?? []).some((c) => !c.eligible) && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wide text-muted mb-1">Not ready yet</div>
                  <ul className="text-xs text-muted space-y-1">
                    {(joinable.data ?? []).filter((c) => !c.eligible).map((c) => (
                      <li key={c.id}><span className="font-mono">{c.code}</span> {c.title}{c.reason ? `: ${c.reason}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => create.mutate()} disabled={!fClient || !fWhen || create.isPending} data-testid="propose-submit">Propose</Button>
            <Button size="sm" variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
            <span className="text-[11px] text-muted self-center">The shoot approver is told at once and says yes or no.</span>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {([['week', 'Next 7 days'], ['fortnight', 'Next 14 days'], ['month', 'Next month'], ['past', 'Past 30 days']] as [Range, string][]).map(([k, label]) => (
          <button key={k} onClick={() => setRange(k)}
            className={cn('text-xs px-3 py-1.5 min-h-[36px] rounded-full border transition',
              range === k ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted hover:text-muted-strong')}>
            {label}
          </button>
        ))}
      </div>
      {wide && (
        <Card className="mb-4">
          <Select label="Clinic" value={clientId} onChange={setClientId} searchable
            options={[{ value: '', label: 'All clinics' }, ...(clients.data ?? []).map((c) => ({ value: c.id, label: c.name }))]} />
        </Card>
      )}

      {shoots.isLoading && <div className="text-muted text-sm">Loading...</div>}
      {!shoots.isLoading && groups.length === 0 && (
        <Card><div className="text-sm text-muted">No shoots in this range.{canPlan ? ' Press New shoot to plan one.' : ''}</div></Card>
      )}

      {groups.map(([day, items]) => (
        <div key={day} className="mb-5">
          <div className="text-xs uppercase tracking-wide text-muted mb-2">{day}</div>
          <div className="space-y-2">
            {items.map((s) => {
              const time = new Date(s.shoot_at).toLocaleTimeString('en-AE', { hour: '2-digit', minute: '2-digit' });
              const notReady = s.creatives.filter((c) => c.script_status !== 'approved').length;
              return (
                <Link key={s.id} to={`/shoots/${s.id}`} className="block">
                  <Card className="hover:border-accent-400/40 transition">
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 w-14 text-center">
                        <div className="text-base font-semibold text-muted-strong tabular-nums">{time}</div>
                        <div className="text-[10px] text-muted uppercase inline-flex items-center gap-0.5"><Camera size={10} /> {s.creative_count}</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-sm text-muted-strong truncate">{s.title}</span>
                          <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', STATUS_CLS[s.status] ?? STATUS_CLS.scheduled)} data-testid="shoot-status">{STATUS_LABEL[s.status] ?? s.status}</span>
                        </div>
                        <div className="text-xs text-muted truncate">{s.client_name}{s.doctors.length ? ` · ${s.doctors.join(', ')}` : ''}</div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted mt-1">
                          {s.location && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {s.location}</span>}
                          <span className="inline-flex items-center gap-1"><Users size={10} /> {s.creative_count} creative{s.creative_count === 1 ? '' : 's'}{notReady ? `, ${notReady} need re-approval` : ''}</span>
                          <span className="inline-flex items-center gap-1"><Video size={10} /> {s.crew.length ? s.crew.map((m) => m.name).join(', ') : 'no crew yet'}</span>
                        </div>
                        {s.creatives.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {s.creatives.slice(0, 8).map((c) => (
                              <span key={c.id} className={cn('font-mono text-[10px] px-1.5 py-0.5 rounded border', c.script_status === 'approved' ? 'border-emerald-500/30 text-emerald-300' : 'border-red-500/30 text-red-300')}>{c.code}</span>
                            ))}
                            {s.creatives.length > 8 && <span className="text-[10px] text-muted">+{s.creatives.length - 8}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
